import React, { useState } from 'react';
import { 
  GraduationCap, Play, Pause, CheckCircle2, Search, Filter, Volume2, 
  Sparkles, Award, FileText, Globe, BookOpen, ShieldCheck, Download, ChevronRight, Activity 
} from 'lucide-react';
import { MODULE_TUTORIALS, ModuleTutorial } from '../data/moduleTutorialData';

export const ModuleTutorialHub: React.FC = () => {
  const [selectedTutorialId, setSelectedTutorialId] = useState<string>(MODULE_TUTORIALS[0].id);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const activeTutorial = MODULE_TUTORIALS.find(t => t.id === selectedTutorialId) || MODULE_TUTORIALS[0];

  const handleTogglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const filteredTutorials = MODULE_TUTORIALS.filter(t => 
    t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto py-4 px-2">
      
      {/* Tutorial Hub Hero Header */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-6 rounded-3xl shadow-xl border border-blue-500/30 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="space-y-2 text-center md:text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/20 text-amber-300 text-xs font-extrabold rounded-full border border-amber-500/30 uppercase tracking-wider">
            <GraduationCap className="w-4 h-4 text-amber-400" />
            <span>Interactive 30-Module Video & Clinical Tutorial Hub</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black">
            Master Every Module, Advantage & Impact
          </h1>
          <p className="text-xs text-blue-200 max-w-2xl">
            Comprehensive operational guides, therapeutic advantages, expected clinical outputs, and regulatory compliance alignment (RPwD Act 2016, NEP 2020, UDID, NDIS, IDEA 2004).
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white/10 p-3 rounded-2xl border border-white/10 backdrop-blur-md shrink-0">
          <div className="text-center px-3 border-r border-white/10">
            <div className="text-xl font-black text-amber-300 font-mono">30 / 30</div>
            <div className="text-[10px] text-blue-200">Modules Covered</div>
          </div>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs rounded-xl shadow transition flex items-center gap-1.5"
          >
            <Download className="w-4 h-4" />
            <span>Export Manual PDF</span>
          </button>
        </div>
      </div>

      {/* Tutorial Explorer Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Sidebar: Module Selector List */}
        <div className="lg:col-span-4 bg-white p-4 rounded-3xl border border-slate-200 shadow-sm space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search module tutorial..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
            {filteredTutorials.map((tut) => {
              const isSelected = selectedTutorialId === tut.id;
              return (
                <button
                  key={tut.id}
                  onClick={() => {
                    setSelectedTutorialId(tut.id);
                    setActiveStep(0);
                    setIsPlaying(false);
                  }}
                  className={`w-full p-3 rounded-2xl border text-left transition flex items-center justify-between gap-3 ${
                    isSelected 
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md ring-2 ring-blue-300' 
                      : 'bg-slate-50 text-slate-800 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`w-7 h-7 rounded-xl font-mono font-black text-xs flex items-center justify-center shrink-0 ${
                      isSelected ? 'bg-amber-400 text-slate-950' : 'bg-slate-200 text-slate-700'
                    }`}>
                      {tut.moduleNumber}
                    </span>
                    <div className="truncate">
                      <div className="text-xs font-extrabold truncate">{tut.title}</div>
                      <div className={`text-[10px] font-medium truncate ${isSelected ? 'text-blue-100' : 'text-slate-500'}`}>
                        {tut.category} • {tut.videoDuration}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 shrink-0 ${isSelected ? 'text-amber-300' : 'text-slate-400'}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Detail Pane: 4 Educational Pillars & Video Player */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* Simulated Interactive Video Player */}
          <div className="bg-slate-950 text-white rounded-3xl border border-slate-800 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs font-extrabold uppercase tracking-wider text-amber-400">
                  Module {activeTutorial.moduleNumber} Video Walkthrough Simulation
                </span>
              </div>
              <span className="text-xs font-mono font-bold text-slate-400">{activeTutorial.videoDuration} HD</span>
            </div>

            {/* Video Stage Screen */}
            <div className="bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl border border-slate-800 p-8 text-center min-h-[220px] flex flex-col items-center justify-center space-y-4 relative overflow-hidden">
              <div className="w-16 h-16 bg-blue-600/30 border-2 border-blue-400/50 rounded-full flex items-center justify-center text-amber-300 shadow-xl">
                <Play className="w-8 h-8 ml-1" />
              </div>

              <div className="space-y-1 max-w-md">
                <h4 className="text-base font-black text-white">
                  Step {activeTutorial.demoVideoSteps[activeStep]?.step || 1}: {activeTutorial.demoVideoSteps[activeStep]?.title}
                </h4>
                <p className="text-xs text-blue-200">
                  {activeTutorial.demoVideoSteps[activeStep]?.description}
                </p>
              </div>

              {/* Step Dots */}
              <div className="flex items-center gap-2 pt-2">
                {activeTutorial.demoVideoSteps.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveStep(idx)}
                    className={`px-3 py-1 rounded-full text-[10px] font-extrabold transition ${
                      activeStep === idx ? 'bg-amber-400 text-slate-950 shadow' : 'bg-slate-800 text-slate-400'
                    }`}
                  >
                    Step {s.step}
                  </button>
                ))}
              </div>
            </div>

            {/* Video Controls Bar */}
            <div className="flex items-center justify-between gap-4 pt-2">
              <button
                onClick={handleTogglePlay}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs flex items-center gap-2 shadow"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                <span>{isPlaying ? 'Pause Simulation' : 'Play Guided Walkthrough'}</span>
              </button>

              <div className="text-xs text-slate-400 font-mono">
                {activeTutorial.title}
              </div>
            </div>
          </div>

          {/* 4 Pillars Grid: Operations, Advantages, Outputs, Regulatory Impact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            {/* PILLAR 1: Operations */}
            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-blue-600 font-black text-sm">
                <BookOpen className="w-4 h-4 text-blue-600" />
                <h3>1. Module Operations & Step-by-Step</h3>
              </div>
              <ul className="space-y-2 text-xs text-slate-700 font-medium">
                {activeTutorial.operations.map((op, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
                    <span>{op}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* PILLAR 2: Clinical Advantages */}
            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-emerald-600 font-black text-sm">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <h3>2. Clinical Advantages & Benefits</h3>
              </div>
              <ul className="space-y-2 text-xs text-slate-700 font-medium">
                {activeTutorial.advantages.map((adv, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{adv}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* PILLAR 3: Clinical Outputs */}
            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-amber-600 font-black text-sm">
                <FileText className="w-4 h-4 text-amber-600" />
                <h3>3. Expected Clinical Outputs</h3>
              </div>
              <ul className="space-y-2 text-xs text-slate-700 font-medium">
                {activeTutorial.clinicalOutputs.map((out, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                    <span>{out}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* PILLAR 4: Regulatory Impact */}
            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-purple-600 font-black text-sm">
                <Globe className="w-4 h-4 text-purple-600" />
                <h3>4. Regulatory Impact & Standards</h3>
              </div>
              <ul className="space-y-2 text-xs text-slate-700 font-medium">
                {activeTutorial.regulatoryImpact.map((reg, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-purple-600 shrink-0 mt-0.5" />
                    <span>{reg}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
};
